import PropTypes from 'prop-types';
import React from 'react';
import { css } from 'emotion';
import Flexbox from 'flexbox-react';
import NavigationUtils from 'utils/NavigationUtils';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import RIEInput from 'components/core/editable/RIEInput';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import RIEUnits from 'components/core/editable/RIEUnits';
import RIENumber from 'components/core/editable/RIENumber';
import EntityHeader from 'components/core/EntityHeader';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import ProcessValidator from 'utils/ProcessValidator';
import ProcessEditor from 'utils/ProcessEditor';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessUtils from 'utils/ProcessUtils';
import ModalsService from 'services/ModalsService';
import Constants from 'utils/Constants';
import InfoMessages from 'components/core/help/InfoMessages';
import AbstractForm from 'components/page/process/AbstractForm';
import DimensionInputDatasetField from 'components/core/fields/DimensionInputDatasetField';
import DataExplorerPage  from 'components/core/data_explorer/DataExplorerPage';
import TransformationTypeParameter from 'components/core/fields/transformation_parameters/TransformationTypeParameter';
import WidthParameter from 'components/core/fields/transformation_parameters/WidthParameter';
import RangeParameter from 'components/core/fields/transformation_parameters/RangeParameter';
import BinAlignmentField from 'components/core/fields/BinAlignmentField';
import _ from 'lodash';
import { colorLightGrey, colorTextGrey } from 'styles/base';

const formStyles = css`
    padding: 8px 15px;
    .last-row-spacer {
         margin-bottom: 10px;
    }
`;

const whitePanel = css`
    background-color: #fff;
    border: 1px solid ${colorLightGrey};
`;

const explorerPageStyles = css`
    margin-top: 30px;
    .data-explorer-children {
        padding-top: 0;
        div.entity-box:first-child {
            margin-top:0;
        }
    }
`;

/**
 * The form that renders details for a coordinate system
 */
export default class CoordinateSystemForm extends AbstractForm {
    static propTypes = {
        id: PropTypes.string,
        process: PropTypes.object,
        revisionNum: PropTypes.string,
        processErrors: PropTypes.object,
        hasWritePermissions: PropTypes.bool,
        getListEntityForEvent: PropTypes.func,
        onFieldUpdated: PropTypes.func,
        getBackgroundColor: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.validator = new ProcessValidator(props.process);
        this._initializePropDependentVariables(this.props);
    }

    componentWillUpdate(nextProps) {
        // Every time our props change, we need to refresh our coord sys, because the process has changed
        this._initializePropDependentVariables(nextProps);
    }

    shouldComponentUpdate(nextProps, nextState) {
        const coordinateSystemName = nextProps.match.params.coordinateSystem;
        const dimensionName = nextProps.match.params.dimension;

        //componentWillReceiveProps is called twice when renaming, first time its called props.routeParams.entity has old name but
        //props.process has new name and so we were getting 'undefined' JS error in console. thats why we're checking for 'undefined' here
        if (nextProps.process.variable_retrieval.coordinate_systems[coordinateSystemName] &&
            nextProps.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName]) {
            return true;
        }
        return false;
    }

    _initializePropDependentVariables(props) {
        this.coordinateSystemName = props.match.params.coordinateSystem;
        this.dimensionName = props.match.params.dimension;
        this.processId = props.match.params.processId;

        this.coordinateSystem = props.process.variable_retrieval.coordinate_systems[this.coordinateSystemName];
        this.dimension = ProcessUtils.getDimension(props.process, this.coordinateSystemName, this.dimensionName);

        this.sortVariableIds(props);

        // base field path is relative to the process
        this.baseFieldPath = ['variable_retrieval', 'coordinate_systems', this.coordinateSystemName];

        // Determine the used variables (i.e., any variable who has been mapped to a coordinate system)
        // Since a variable can only be mapped to one coordinate system, we don't allow users to select a variable if it belongs
        // to another one.
        this.usedVars = [];
        Object.keys(props.process.variable_retrieval.retrieved_variables).forEach(variableName => {
            const variable = props.process.variable_retrieval.retrieved_variables[variableName];
            if (variable.coordinate_system) {
                this.usedVars.push(variableName);
            }
        });

        // Only show the output datastreams that have been mapped to variables using this coordinate system
        this.outputDatastreams = [];
        const mappings = props.process.variable_retrieval.output_datastream_variable_mappings;
        this.retrievedVariables.forEach(variableName => {
            Object.keys(mappings).forEach(outputDatastreamName => {
                const variableNames = Object.keys(mappings[outputDatastreamName]);
                variableNames.forEach(otherVariableName => {
                    if (variableName === otherVariableName) {
                        if (!_.includes(this.outputDatastreams, outputDatastreamName)) {
                            this.outputDatastreams.push(outputDatastreamName);
                        }
                    }
                });
            });
        });

        this.checkUnique = {
            label: 'coordinate system',
            list: Object.keys(props.process.variable_retrieval.coordinate_systems)
        };

        this.checkDimensionUnique = {
            label: 'dimension',
            list: ProcessUtils.getDimensionsAsArray(props.process, this.coordinateSystemName)
        };
    }

    onRemoveDimensionClicked = () => {
        const dimensionToDelete = this.dimension.name;

        // If the coordinate system has mapped variables that use this dimension, then the user
        // can't delete the dimension until they remove the mappings
        let mappedVariables = [];
        const variables = Object.values(this.props.process.variable_retrieval.retrieved_variables);
        variables.forEach(variable => {
            if (variable.coordinate_system === this.coordinateSystem.name &&
                variable.dims && _.includes(variable.dims, dimensionToDelete)) {
                mappedVariables.push(variable);
            }
        });

        if (mappedVariables.length > 0) { // delete is prevented
            const messageArray = [];

            mappedVariables.forEach(variable => {
                messageArray.push(
                    <li style={{ marginBottom: '3px' }} key={variable.name}>
                        <strong>{variable.name}</strong>
                        <span className="sub-text">({variable.dims.join(', ')})</span>
                    </li>
                );
            });

            const message = (
                <div>
                    <p>You may not delete dimension <strong>{dimensionToDelete}</strong> because the following
                        variable(s) are currently mapped to this coordinate system.
                        In order to delete this dimension, you must first detach these variables.
                    </p>
                    <ul style={{ height: '200px', overflow: 'auto' }}>
                        {messageArray}
                    </ul>
                </div>
            );
            ModalsService.openModal({
                id: 'dimension_delete_forbidden',
                modalType: ModalsService.TYPE_ERROR,
                modalTitle: 'Delete Dimension Forbidden',
                modalMessage: message
            });

        } else { // confirm delete

            const deleteOk = () => {
                const editor = ProcessUtils.getEditor(this.props.process);
                editor.removeDimension(this.coordinateSystem.name, dimensionToDelete);
                editor.commit();

                NavigationUtils.gotoVariableRetrieval(this.props.processId, this.props.revisionNum);
            };
            ModalsService.openModal({
                id: 'confirm_remove_dimension',
                modalType: ModalsService.TYPE_CONFIRM,
                modalTitle: 'Confirm Delete',
                modalMessage: `Are you sure you want to remove dimension ${dimensionToDelete}?`,
                autoFocus: true,
                onOk: deleteOk
            });
        }
    };

    onDeleteCoordinateSystemClicked = () => {
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'variable_retrieval.coordinate_systems', this.coordinateSystemName,
            this.validator.canCoordSystemsBeDeleted, true);
    };

    onNewDimensionClicked = () => {
        ComponentUtils.openNewEntityModal('Dimension',
            (newName) => {
                const editor = ProcessUtils.getEditor(this.props.process);
                editor.initializeDimension(this.coordinateSystem.name, newName);
                editor.commit();

                NavigationUtils.gotoCoordinateSystem(this.processId, this.props.revisionNum, this.coordinateSystem.name, newName);
            },
            this.isDimensionNameValid, { inputLabel: 'Name' });
    };

    /**
     * Called when a regular grid field changes, since they are computed relative to each other
     * @param fieldPath
     * @param value
     */
    onRegularGridChanged = (fieldPath, value) => {
        const dataTransformation = _.cloneDeep(this.dimension.data_transformation);
        value = Number(value);

        const fieldPathEnd = fieldPath[fieldPath.length - 1];

        if (fieldPathEnd === 'interval') {
            dataTransformation.interval = value;

            if (dataTransformation.interval && dataTransformation.length > 0) {
                dataTransformation.end = ProcessUtils.calculateGridEnd(dataTransformation.interval, dataTransformation.length, dataTransformation.start, dataTransformation.end);
            }
            if (dataTransformation.end !== 0) {
                // if end is valid, then compute number of bins
                dataTransformation.length = ProcessUtils.calculateGridNumberOfIntervals(dataTransformation.interval, dataTransformation.start, dataTransformation.end);
            }

        } else if (fieldPathEnd === 'length') {
            dataTransformation.length = value;

            if (dataTransformation.interval && dataTransformation.length > 0) {
                dataTransformation.end = ProcessUtils.calculateGridEnd(dataTransformation.interval, dataTransformation.length, dataTransformation.start, dataTransformation.end);
            }

            dataTransformation.interval = ProcessUtils.calculateGridInterval(dataTransformation.interval, dataTransformation.length, dataTransformation.start, dataTransformation.end);

        } else if (fieldPathEnd === 'start') {
            dataTransformation.start = value;
            dataTransformation.end = ProcessUtils.calculateGridEnd(dataTransformation.interval, dataTransformation.length, dataTransformation.start, dataTransformation.end);

        } else if (fieldPathEnd === 'end') {
            dataTransformation.end = value;

            // Compute numBins only if numbers are valid
            if (!isNaN(dataTransformation.interval) && !isNaN(dataTransformation.start) && !isNaN(dataTransformation.end)) {
                dataTransformation.length = ProcessUtils.calculateGridNumberOfIntervals(dataTransformation.interval, dataTransformation.start, dataTransformation.end);
            }
        }

        const dtFieldPath = [...this.baseFieldPath, 'dimensions', this.dimension.name, 'data_transformation'];
        this.onFieldUpdated(dtFieldPath, dataTransformation);
    };

    onGriddingMethodChanged = (fieldPath, value) => {
        const dimensionName = this.dimension.name;
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.updateGriddingMethod(this.coordinateSystem.name, dimensionName, value);
        editor.commit();
    };

    /**
     * Called when the coordinate system name changes.
     * @param fieldPath
     * @param value
     */
    onCoordinateNameChanged = (fieldPath, value) => {
        const oldName = this.coordinateSystem.name;
        //should this be done in ProcessUtils?
        ComponentUtils.renameSessionStorageKey(this.props.id, oldName, value);
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.renameCoordinateSystem(oldName, value, true);
        editor.commit();
    };

    /**
     * Called when a dimension name changes.
     * @param fieldPath
     * @param value
     */
    onDimensionNameChanged = (fieldPath, value) => {
        const oldName = this.dimension.name;

        //ComponentUtils.renameSessionStorageKey(this.props.id, oldName, value);
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.renameDimension(this.coordinateSystem.name, oldName, value, true);
        editor.commit();
    };

    onInputDatasetChanged = (fieldPath, valueObject) => {
        const inputDatasetName = valueObject.value;
        const coordinateSystemName = this.coordinateSystem.name;
        const dimensionName = this.dimension.name;
        const editor = new ProcessEditor(this.props.process);
        editor.updateDimensionInputDataset(coordinateSystemName, dimensionName, inputDatasetName);
        editor.commit();
    };

    onCoordinateVariableChanged = (fieldPath, value) => {
        const coordinateSystemName = this.coordinateSystem.name;
        const dimensionName = this.dimension.name;
        const inputDatasetName = this.coordinateSystem.dimensions[dimensionName].input_dataset;
        const coordinateVariables = value.map((valueObj) => {
            return valueObj.value;
        });

        const editor = new ProcessEditor(this.props.process);
        editor.setCoordinateVariablesPerRule(coordinateSystemName, dimensionName, inputDatasetName, coordinateVariables);
        editor.commit();
    };

    isDimensionNameValid = (name, checkUnique) => {
        const existingList = checkUnique ? Object.keys(this.coordinateSystem.dimensions) : null;
        return this.validator.isEntityNameValid(name, existingList, 'Dimension');
    };

    /**
     * Get the list of retrieved variables that are mapped to this coordinate system
     */
    sortVariableIds(props) {
        this.retrievedVariables = [];
        const coordinateSystemName = props.match.params.coordinateSystem;

        if (props.process) {
            const retrievedVariables = props.process.variable_retrieval.retrieved_variables;

            Object.keys(retrievedVariables).forEach((variableId) => {
                // If the variable has our coordinate system, then add it
                const retrievedVariable = retrievedVariables[variableId];
                if (retrievedVariable.coordinate_system === coordinateSystemName) {
                    this.retrievedVariables.push(variableId);
                }
            });

            // Sort the list in alphabetical order
            this.retrievedVariables = this.retrievedVariables.sort((a, b) => {
                return a.toLowerCase().localeCompare(b.toLowerCase());
            });
        }
    }

    getErrorClass(dimensionName) {
        const fieldPath = [...this.baseFieldPath, 'dimensions', dimensionName];
        return ProcessValidator.getErrorClassForField(this.props.processErrors, fieldPath);
    }

    getUnits(dimension) {
        let value = Constants.getOptionById(dimension.coordinate_variable_units, Constants.COORDINATE_SYSTEM_UNITS);
        if (value === null) {
            value = { value: dimension.coordinate_variable_units, label: dimension.coordinate_variable_units };
        }
        return value;
    }

    getGriddingMethod(dimension) {
        return Constants.getOptionById(dimension.gridding_method, Constants.COORDINATE_SYSTEM_GRIDDING_METHODS);
    }

    getTransformType(dimension) {
        return Constants.getOptionById(dimension.data_transformation.transformation_type, Constants.COORDINATE_SYSTEM_TRANSFORM_TYPES);
    }

    getDataType(dimension) {
        return Constants.getOptionById(dimension.coordinate_variable_data_type, Constants.COORDINATE_SYSTEM_DATA_TYPES);
    }

    renderDimensionInfo() {
        const baseDimensionPath = [...this.baseFieldPath, 'dimensions', this.dimension.name];
        const labelWidth = '105px';

        return (
            <Flexbox flexDirection="column">
                {/* Type */}
                <Flexbox flexDirection="row" marginBottom="10px" alignItems="flex-start">
                    <InfoBoxLabel label="Data Type" width={labelWidth} />
                    <RIEComboBox
                        value={this.getDataType(this.dimension)}
                        options={Constants.COORDINATE_SYSTEM_DATA_TYPES}
                        change={this.onFieldUpdated}
                        fieldPath={[...baseDimensionPath, 'coordinate_variable_data_type']}
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                        minWidth="100%"
                    />
                </Flexbox>
                {/* Units */}
                <Flexbox flexDirection="row" marginBottom="10px" alignItems="flex-start">
                    <InfoBoxLabel label="Units" width={labelWidth} />
                    <RIEUnits
                        value={this.getUnits(this.dimension)}
                        change={this.onFieldUpdated}
                        fieldPath={[...baseDimensionPath, 'coordinate_variable_units']}
                        clearable={false}
                        options={Constants.COORDINATE_SYSTEM_UNITS}
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                    />
                </Flexbox>
            </Flexbox>
        );
    }

    renderMappingParameters(dimension, baseDimensionPath) {
        const inputDatasetName = dimension.input_dataset;
        let coordinateVariableValue = null;
        let coordinateVariableOptions = [];

        if (inputDatasetName) {
            const datastreamName = ProcessUtils.getPrimaryDatastream(this.props.process, inputDatasetName);
            coordinateVariableOptions = ProcessUtils.getCoordinateVariableOptionsForDatastream(datastreamName);

            if (dimension.coordinate_variables_per_rule.length > 0) {
                const mappedVariables = dimension.coordinate_variables_per_rule[0];
                if (mappedVariables) {
                    coordinateVariableValue = mappedVariables.map((v) => {
                        return { value: v, label: v };
                    });
                }
            }
        }
        const dimensionName = this.dimension.name;
        return (
            <Flexbox flexDirection="column" marginTop="10px" flexGrow={1}>
                {/* Input Dataset */}
                <DimensionInputDatasetField
                    coordinateSystemName={this.coordinateSystem.name}
                    dimensionName={dimensionName}
                    process={this.props.process}
                    processId={this.props.process.name}
                    processErrors={this.props.processErrors}
                    displayLabel
                />
                <Flexbox className="coordinate-variable" flexDirection="row" alignItems="flex-start">
                    <InfoBoxLabel label="Coordinate Variable Name(s)" />
                    <RIEComboBox
                        value={coordinateVariableValue}
                        options={coordinateVariableOptions}
                        change={this.onCoordinateVariableChanged}
                        fieldPath={[...baseDimensionPath, 'coordinate_variables_per_rule']}
                        isDisabled={!this.props.hasWritePermissions}
                        multi
                        clearable
                        creatable
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                    />

                </Flexbox>
                <Flexbox className={css`color:${colorTextGrey}; margin: 5px 0 10px 10px`}>
                    <em>View the <a onClick={NavigationUtils.gotoRetrievalRulesFunc(this.props.process.name)}>Retrieval Rules</a> to see how the process will look for the coordinate variable</em>
                </Flexbox>
            </Flexbox>
        );
    }

    renderRegularGridParameters(dimension, baseDimensionPath) {
        return (
            <Flexbox flexDirection="column" flexGrow={1}>
                {/* Bin Length */}
                <Flexbox flexDirection="row" alignItems="flex-start" flexGrow={1} marginRight="5px">
                    <InfoBoxLabel label="Interval" message={InfoMessages.dimensionInterval} />
                    <RIENumber
                        value={dimension.data_transformation.interval}
                        change={this.onRegularGridChanged}
                        fieldPath={[...baseDimensionPath, 'data_transformation', 'interval']}
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                    />
                </Flexbox>

                {/* Start */}
                <Flexbox flexDirection="row" alignItems="flex-start" flexGrow={1} marginRight="5px">
                    <InfoBoxLabel label="Start" message={InfoMessages.dimensionStart} />
                    <RIENumber
                        value={dimension.data_transformation.start}
                        change={this.onRegularGridChanged}
                        fieldPath={[...baseDimensionPath, 'data_transformation', 'start']}
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                    />
                </Flexbox>

                {/* End */}
                <Flexbox flexDirection="row" alignItems="flex-start" flexGrow={1} marginRight="5px">
                    <InfoBoxLabel label="End" message={InfoMessages.dimensionEnd} />
                    <RIENumber
                        value={dimension.data_transformation.end}
                        change={this.onRegularGridChanged}
                        fieldPath={[...baseDimensionPath, 'data_transformation', 'end']}
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                    />
                </Flexbox>

                {/* Length of Dimension */}
                <Flexbox flexDirection="row" alignItems="flex-start" flexGrow={1}>
                    <InfoBoxLabel label="Number of intervals" message={InfoMessages.dimensionLength} />
                    <RIENumber
                        value={dimension.data_transformation.length}
                        change={this.onRegularGridChanged}
                        fieldPath={[...baseDimensionPath, 'data_transformation', 'length']}
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                    />
                </Flexbox>
            </Flexbox>
        );
    }

    renderGridShape() {
        const baseDimensionPath = [...this.baseFieldPath, 'dimensions', this.dimension.name];
        let griddingMethodText = null;
        let griddingParameters = null;

        if (this.dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID) {
            griddingMethodText = `${this.dimension.name} values will be defined using the following parameters:`;
            griddingParameters = this.renderRegularGridParameters(this.dimension, baseDimensionPath);

        } else if (this.dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_SET_AT_RUNTIME) {
            griddingMethodText = `${this.dimension.name} values will be computed dynamically in the software project`;

        } else if (this.dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING) {
            griddingMethodText = `${this.dimension.name} values will exactly match the given input datastream`;
            griddingParameters = this.renderMappingParameters(this.dimension, baseDimensionPath);
        }

        const options = Constants.COORDINATE_SYSTEM_GRIDDING_METHODS;
        const padding = css`padding:8px 15px;`;
        return (
            <Flexbox flexDirection="column" className="entity-box grid-shape" marginTop="20px">
                <EntityHeader title="Dimension Shape" />
                <Flexbox flexDirection="column" className={`${whitePanel} ${padding}`}>
                    {/* Gridding Method */}
                    <Flexbox flexDirection="row" alignItems="center">
                        <InfoBoxLabel label={`How should ${this.dimension.name} coordinate data be determined?`} hideColon />
                        <RIEComboBox
                            value={this.getGriddingMethod(this.dimension)}
                            options={options}
                            change={this.onGriddingMethodChanged}
                            fieldPath={[...baseDimensionPath, 'gridding_method']}
                            isDisabled={!this.props.hasWritePermissions}
                            processId={this.props.process.name}
                            style={{ marginLeft: '8px' }}
                        />
                    </Flexbox>
                    <Flexbox flexDirection="row" alignItems="flex-start" margin="5px 0 10px 10px" className={css`color:${colorTextGrey}`}>
                        <em>{griddingMethodText}</em>
                    </Flexbox>
                    <Flexbox flexDirection="row" alignItems="flex-start" flexGrow={1}>
                        {griddingParameters}
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
    }

    renderGridShapeNew() {
        const baseDimensionPath = [...this.baseFieldPath, 'dimensions', this.dimension.name];

        let griddingMethodText = null;
        let griddingParameters = null;

        if (this.dimension.gridding_method === 'regular_grid') {
            griddingMethodText = 'Coordinate variable points will be defined using the following parameters:';
            griddingParameters = this.renderRegularGridParameters(this.dimension, baseDimensionPath);

        } else if (this.dimension.gridding_method === 'passthrough') {
            griddingMethodText = 'Coordinate variable points will exactly match the input datastream';

        } else { // mapping
            griddingMethodText = 'Coordinate variable points will match coordinate variable for the given datastream';
            griddingParameters = this.renderMappingParameters(this.dimension, baseDimensionPath);
        }

        const options = Constants.COORDINATE_SYSTEM_GRIDDING_METHODS;
        return (
            <Flexbox flexDirection="column" className="entity-box grid-shape" marginTop="20px">
                <Flexbox flexDirection="column">
                    {/* Gridding Method */}
                    <Flexbox flexDirection="row" alignItems="flex-start">
                        <InfoBoxLabel label="Dimension Shape" width="175px" labelWrap />
                        <RIEComboBox
                            value={this.getGriddingMethod(this.dimension)}
                            options={options}
                            change={this.onGriddingMethodChanged}
                            fieldPath={[...baseDimensionPath, 'gridding_method']}
                            isDisabled={!this.props.hasWritePermissions}
                            processId={this.props.process.name}
                        />
                    </Flexbox>
                    <Flexbox flexDirection="row" alignItems="flex-start" margin="5px 0 10px 40px">
                        <em>{griddingMethodText}</em>
                    </Flexbox>
                    <Flexbox flexDirection="row" alignItems="flex-start" marginLeft="40px" flexGrow={1}>
                        {griddingParameters}
                    </Flexbox>

                </Flexbox>
            </Flexbox>
        );
    }

    renderTransformation() {
        return (
            <Flexbox flexDirection="column" className="entity-box" marginTop="20px">
                <EntityHeader title="Transformation" />
                <Flexbox flexDirection="column" className={whitePanel}>
                    <Flexbox className={formStyles} flexDirection={'column'}>
                        <TransformationTypeParameter
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            coordinateSystemName={this.coordinateSystemName}
                            dimensionName={this.dimensionName}
                            infoBoxText={InfoMessages.transformationType}
                            hasWritePermissions={this.props.hasWritePermissions}
                        />
                    </Flexbox>
                    <Flexbox className={formStyles} flexDirection={'column'}>
                        <BinAlignmentField
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            coordinateSystemName={this.coordinateSystemName}
                            dimensionName={this.dimensionName}
                            hasWritePermissions={this.props.hasWritePermissions}
                        />
                    </Flexbox>
                    <Flexbox className={formStyles} flexDirection={'column'}>
                        <WidthParameter
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            coordinateSystemName={this.coordinateSystemName}
                            dimensionName={this.dimensionName}
                            hasWritePermissions={this.props.hasWritePermissions}
                        />
                    </Flexbox>
                    <Flexbox className={formStyles} flexDirection={'column'}>
                        <RangeParameter
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            coordinateSystemName={this.coordinateSystemName}
                            dimensionName={this.dimensionName}
                            hasWritePermissions={this.props.hasWritePermissions}
                        />
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
    }

    render() {
        const pageId = `${this.props.process.name}-coordinate-system-${this.coordinateSystem.name}`;
        const dimIds = Object.keys(this.coordinateSystem.dimensions);
        let panelMargin = '0';
        if (dimIds.length === 0) {
            panelMargin = '31px';
        }
        const emptyClass = dimIds.length > 0 ? '' : 'empty';

        return (
            <Flexbox className="coordinate-system-form" flexDirection="row" flexGrow={1}>
                {/* Left Column - Dimensions */}
                <Flexbox flexDirection="column" className="entity-box dimensions" marginLeft="-10px">
                    {/* Name */}
                    <Flexbox flexDirection="row" marginBottom="5px" marginLeft="10px" alignItems="flex-start" className="noshrink">
                        <InfoBoxLabel label="Coordinate System Name" />
                        <RIEInput
                            className="entity-name"
                            value={this.coordinateSystem.name}
                            change={this.onCoordinateNameChanged}
                            fieldPath={['variable_retrieval', 'coordinate_systems', this.coordinateSystem.name, 'name']}
                            isDisabled={!this.props.hasWritePermissions}
                            urlCommit
                            checkUnique={this.checkUnique}
                            processId={this.props.process.name}
                            processErrors={this.props.processErrors}
                        />
                        <Toolbar>
                            <ToolbarItem
                                title="New dimension"
                                className="fa fa-plus new"
                                clickHandler={this.onNewDimensionClicked}
                                enabled={this.props.hasWritePermissions}
                                showText
                            />
                            {this.dimension.name !== 'time' &&
                                <ToolbarItem
                                    title="Delete dimension"
                                    className="fa fa-times delete"
                                    clickHandler={this.onRemoveDimensionClicked}
                                    enabled={this.props.hasWritePermissions}
                                    showText
                                />
                            }
                            <ToolbarItem
                                title="Delete coordinate system"
                                className="fa fa-times delete"
                                clickHandler={this.onDeleteCoordinateSystemClicked}
                                enabled={this.props.hasWritePermissions}
                                showText
                            />
                        </Toolbar>
                    </Flexbox>
                    <Flexbox flexDirection="row" marginBottom="20px" marginLeft="10px" alignItems="flex-start" className="noshrink">
                        <InfoBoxLabel label="Dimension Name"/>
                        <RIEInput
                            className="entity-name"
                            value={this.dimension.name}
                            change={this.onDimensionNameChanged}
                            fieldPath={['variable_retrieval', 'coordinate_systems', this.coordinateSystem.name, 'name']} //TODO: test this
                            isDisabled={!this.props.hasWritePermissions}
                            urlCommit
                            checkUnique={this.checkDimensionUnique}
                            processId={this.props.process.name}
                            processErrors={this.props.processErrors}
                        />
                    </Flexbox>
                    <Flexbox flexDirection="column" flexGrow={1} marginTop={panelMargin} className={`form-panel dimensions ${emptyClass}`}>
                        <Flexbox flexDirection="column">
                            {this.renderDimensionInfo()}
                            <DataExplorerPage
                                process={this.props.process}
                                siteFacilities={this.props.siteFacilities}
                                exploredDatastreams={this.props.exploredDatastreams}
                                className={explorerPageStyles}
                            >
                                {this.renderGridShape()}
                                {this.renderTransformation()}
                            </DataExplorerPage>
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/coordinateSystem/CoordinateSystemForm.js